import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ArrowLeft, ArrowRight, GraduationCap, Heart, LockKeyhole, Menu, UserRound, X } from 'lucide-react';
import Button from '../Button';
import Hyperlink from '../Hyperlink';
import NavigationCard from '../NavigationCard';
import Price from '../Price';
import SideMenu from '../SideMenu';
import TopBar from '../TopBar';
import useDrawerSwipe from '../hooks/useDrawerSwipe';
import useHideOnScroll from '../hooks/useHideOnScroll';
import BRAND_LOGOS from '../assets/logos';
import CartIcon from '../CartIcon';
import './Header.css';
import { drawerDurationMs, readCssDurationMs } from '../utils/cssDuration';

/* Custom hamburger icon from Figma (custom/24/hamburger-menu, Overall header 4150:4507) */
const HamburgerMenuIcon = require('../assets/common/icon-hamburger-menu.svg').ReactComponent;

const normalizeSubBrand = (subBrand) => {
  const normalized = String(subBrand || 'main').toLowerCase();

  if (normalized === 'clothing') return 'clothing';
  return 'main';
};

/* Every string the header speaks, with English defaults; a brand's
   BRAND_CONTENT.labels and the `labels` prop override them */
const DEFAULT_LABELS = {
  cart: 'Cart',
  items: 'Items',
  close: 'Close',
  checkout: 'Checkout',
  menu: 'Menu',
  back: 'Back',
  closeMenu: 'Close menu',
  nav: 'Header navigation',
  mobileNav: 'Mobile navigation',
  logo: 'Home',
};

const BRAND_CONTENT = {
  CLS: {
    navItems: ['STUDIETURTRØJE', 'STUDENTERUGENTRØJE', 'STUDENTERHUE'],
    navVariant: 'small',   // a.Small in Figma (12px, medium, uppercase content)
    schoolLabel: 'VÆLG SKOLE',
    labels: { items: 'Varer', checkout: 'Til Kassen', close: 'Luk' },
  },
  ABC: {
    navItems: ['Skolmerch', 'Overalls & More', 'Student Club', 'Student Cap'],
    navVariant: 'small',   // a.Small in Figma (12px, medium, uppercase content)
    schoolLabel: 'VÄLJ SKOLA',
    labels: { items: 'Varor', checkout: 'Till kassan', close: 'Stäng' },
  },
  /* Figma Header/Clothing Brand=Overall: Desktop 4150:4507, Mobile 4150:4525,
     Checkout Desktop 4150:4536 / Mobile 4150:4547. Wears the `centered` layout
     (hamburger + nav left, logo centred, wishlist / account / cart right) */
  Overall: {
    preset: 'centered',
    navItems: ['OVERALLS', 'PATCH UNIVERSE'],
    navVariant: 'small',
    schoolLabel: 'VÄLJ SKOLA',
    labels: { items: 'Varor', checkout: 'Till kassan', close: 'Stäng', menu: 'Meny', closeMenu: 'Stäng meny' },
    // Tertiary/Text hamburger with the custom icon; it stays on desktop (left of the
    // nav) and opens the drawer there too — the `menuIcon` / `menuOnDesktop` props
    // do the same for any brand
    menuVariant: 'text',
    menuIcon: <HamburgerMenuIcon className="ds-header__menu-icon" aria-hidden="true" />,
    desktopDrawer: true,
    // Right navigation: wishlist + account + cart, no school selector
    showSchoolButton: false,
    showWishlistButton: true,
    showUserButton: true,
  },
  /* Crownstudent (Crown Student, sv) — Figma Header/Clothing Brand=Crownstudent:
     Desktop 4430:1697, Mobile 4430:1716, Checkout Desktop 4430:1730 / Mobile 4430:1741.
     Same layout as OVER/LL, so `preset` picks the `ds-header--centered` layout in
     Header.css; the brand differs in copy, tokens and the crown logo's size. */
  Crownstudent: {
    preset: 'centered',
    navItems: ['STUDENTMÖSSA', 'CHAMPAGNEGLAS', 'STUDENTPRYLAR'],
    // custom/48/crown-logo (100×44): 81px in the mobile bar, 72px in the mobile
    // checkout bar, 100px on desktop — read by the layout as --header-logo-width
    logoWidth: { mobile: '81px', checkoutMobile: '72px', desktop: '100px' },
    navVariant: 'small',
    schoolLabel: 'VÄLJ SKOLA',
    labels: { items: 'Varor', checkout: 'Till kassan', close: 'Stäng', menu: 'Meny', closeMenu: 'Stäng meny' },
    menuVariant: 'text',
    menuIcon: <HamburgerMenuIcon className="ds-header__menu-icon" aria-hidden="true" />,
    desktopDrawer: true,
    showSchoolButton: false,
    showWishlistButton: true,
    showUserButton: true,
  },
};

const Header = ({
  brand = 'CLS',
  subBrand = 'main',
  logoHref,
  onLogoClick,
  schoolLabel,
  mode = 'default',
  showTopBar = false,
  topBarNotice,
  topBarLinks,
  hideOnScroll = false,
  cartCount: cartCountProp,
  cartItems: cartItemsProp,
  cartTotal: cartTotalProp,
  cartItemsLabel,
  cartTotalLabel = 'Total',
  cartCtaLabel,
  onCartCtaClick,
  navItems: navItemsProp,
  mobileSettings,
  onMenuClick,
  sideMenu,
  menuIcon: menuIconProp,
  menuOnDesktop: menuOnDesktopProp,
  logoSrc: logoSrcProp,
  labels: labelsProp,
  routeKey,
  onSchoolClick,
  showSchoolButton: showSchoolButtonProp,
  showWishlistButton: showWishlistButtonProp,
  onWishlistClick,
  wishlistLabel = 'Wishlist',
  showUserButton: showUserButtonProp,
  showCartButton = true,
  onUserClick,
  orderLabel = 'Edit order',
  onBackClick,
  checkoutLabel = 'Secure Checkout',
  backLabel = 'Back',
  className = '',
}) => {
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [displayedSubmenu, setDisplayedSubmenu] = useState(null);
  const [isExpanderClosing, setIsExpanderClosing] = useState(false);
  const [isExpanderSwitching, setIsExpanderSwitching] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileSubmenuIndex, setMobileSubmenuIndex] = useState(null);
  const [mobileMenuDirection, setMobileMenuDirection] = useState(null); // 'forward' | 'back'
  const [drawerEntered, setDrawerEntered] = useState(false);
  const [isDrawerClosing, setIsDrawerClosing] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const closeTimeoutRef = useRef(null);
  const switchIntentTimerRef = useRef(null);
  const expanderCloseTimerRef = useRef(null);
  const drawerCloseTimerRef = useRef(null);

  /* Derive cartCount from items if not explicitly set */
  const cartItems = cartItemsProp || [];
  const cartCount = cartCountProp ?? cartItems.length;

  /* ─── Listen for open-minicart custom event ─── */
  useEffect(() => {
    const handleOpenMinicart = () => {
      if (cartItems.length > 0) setIsCartOpen(true);
      else if (onCartCtaClick) onCartCtaClick();
    };
    document.addEventListener('open-minicart', handleOpenMinicart);
    return () => document.removeEventListener('open-minicart', handleOpenMinicart);
  }, [cartItems.length, onCartCtaClick]);

  /* ─── Sync displayedSubmenu for exit / switch / enter animation ─── */
  useEffect(() => {
    if (expanderCloseTimerRef.current) {
      clearTimeout(expanderCloseTimerRef.current);
      expanderCloseTimerRef.current = null;
    }

    if (activeSubmenu !== null && displayedSubmenu !== null && activeSubmenu !== displayedSubmenu) {
      // Switching between tabs — quick cross-fade
      setIsExpanderClosing(false);
      setIsExpanderSwitching(true);
      expanderCloseTimerRef.current = setTimeout(() => {
        setDisplayedSubmenu(activeSubmenu);
        setIsExpanderSwitching(false);
      }, hoverMs());
    } else if (activeSubmenu !== null) {
      // Opening (from null) — instant show + enter animation via CSS
      setIsExpanderClosing(false);
      setIsExpanderSwitching(false);
      setDisplayedSubmenu(activeSubmenu);
    } else if (displayedSubmenu !== null) {
      // Closing — play exit animation, then unmount
      setIsExpanderSwitching(false);
      setIsExpanderClosing(true);
      expanderCloseTimerRef.current = setTimeout(() => {
        setDisplayedSubmenu(null);
        setIsExpanderClosing(false);
      }, drawerDurationMs() / 2); // ds-header-expander-out runs for half the drawer time
    }
    return () => {
      if (expanderCloseTimerRef.current) {
        clearTimeout(expanderCloseTimerRef.current);
      }
    };
  }, [activeSubmenu]); // eslint-disable-line react-hooks/exhaustive-deps

  const content = BRAND_CONTENT[brand] || BRAND_CONTENT.CLS;
  // Header.css lays out --cls / --abc / --centered; a brand without its own entry
  // reuses the CLS layout, like it already reuses its copy and logo, and a brand
  // whose entry names a `preset` wears that layout (Overall, Crownstudent → centered).
  const layoutBrand = BRAND_CONTENT[brand] ? brand : 'CLS';
  const layoutPreset = content.preset || layoutBrand;
  // Explicit props win; a handler shows its button (appearance follows the
  // data); otherwise the brand decides which right-hand buttons exist
  const showSchoolButton = showSchoolButtonProp ?? content.showSchoolButton ?? true;
  const showWishlistButton = showWishlistButtonProp ?? (onWishlistClick ? true : content.showWishlistButton ?? false);
  const showUserButton = showUserButtonProp ?? (onUserClick ? true : content.showUserButton ?? false);
  const menuOnDesktop = menuOnDesktopProp ?? Boolean(content.desktopDrawer);
  const menuIcon = menuIconProp || content.menuIcon || <Menu size={20} />;
  const labels = { ...DEFAULT_LABELS, ...content.labels, ...labelsProp };
  const normalizedSubBrand = normalizeSubBrand(subBrand);
  const logoSrc = logoSrcProp || BRAND_LOGOS[brand]?.[normalizedSubBrand] || BRAND_LOGOS.CLS.main;
  const isCheckout = mode === 'checkout';
  // Half the hyperlink transition — the hover-intent dwell and the expander cross-fade
  const hoverMs = () => readCssDurationMs('--transition-time-hyperlink', 300) / 2;

  /* Normalize navItems: props override, otherwise fall back to simple strings */
  const navItems = navItemsProp || content.navItems.map((label) => ({ label, href: '#' }));

  /* ─── Lock body scroll when mobile menu is open ─── */
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  /* ─── Disable CSS entrance animation after it completes (for swipe transforms) ─── */
  useEffect(() => {
    if (isMobileMenuOpen && !isDrawerClosing) {
      setDrawerEntered(false);
      const timer = setTimeout(() => setDrawerEntered(true), drawerDurationMs() + 50);
      return () => clearTimeout(timer);
    } else if (!isMobileMenuOpen) {
      setDrawerEntered(false);
    }
  }, [isMobileMenuOpen, isDrawerClosing]);

  /* ─── Cleanup hover/close timers on unmount ─── */
  useEffect(() => {
    return () => {
      if (drawerCloseTimerRef.current) clearTimeout(drawerCloseTimerRef.current);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
      if (switchIntentTimerRef.current) clearTimeout(switchIntentTimerRef.current);
    };
  }, []);

  /* ─── Mobile menu handlers ─── */
  const openMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(true);
    setMobileSubmenuIndex(null);
  }, []);

  const closeMobileMenu = useCallback((immediate) => {
    if (isDrawerClosing) return;

    if (immediate === true) {
      setIsMobileMenuOpen(false);
      setMobileSubmenuIndex(null);
      setIsDrawerClosing(false);
      setDrawerEntered(false);
      return;
    }

    setIsDrawerClosing(true);
    setDrawerEntered(false);
    drawerCloseTimerRef.current = setTimeout(() => {
      setIsMobileMenuOpen(false);
      setMobileSubmenuIndex(null);
      setIsDrawerClosing(false);
    }, drawerDurationMs());
  }, [isDrawerClosing]);

  const openMobileSubmenu = useCallback((index) => {
    setMobileMenuDirection('forward');
    setMobileSubmenuIndex(index);
  }, []);

  const goBackMobileMenu = useCallback(() => {
    setMobileMenuDirection('back');
    setMobileSubmenuIndex(null);
  }, []);

  /* ─── Drawer swipe gestures ─── */
  const { drawerRef, drawerSwipeStyle, overlaySwipeStyle, isSwiping } = useDrawerSwipe({
    isOpen: isMobileMenuOpen,
    isSubmenu: mobileSubmenuIndex !== null,
    onClose: closeMobileMenu,
    onBack: goBackMobileMenu,
  });

  /* ─── Hover handlers for mega-menu ─── */
  const clearSwitchIntentTimer = useCallback(() => {
    if (switchIntentTimerRef.current) {
      clearTimeout(switchIntentTimerRef.current);
      switchIntentTimerRef.current = null;
    }
  }, []);

  const handleNavEnter = useCallback(
    (index) => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      clearSwitchIntentTimer();
      const target = navItems[index]?.submenu ? index : null;
      if (activeSubmenu === null) {
        // Nothing open — react instantly
        setActiveSubmenu(target);
      } else if (target !== activeSubmenu) {
        // A submenu is open: the pointer may just be passing over this item
        // on a diagonal toward the expander. Only switch/close after a short
        // dwell; the per-item mouseleave cancels a pass-through.
        switchIntentTimerRef.current = setTimeout(() => {
          switchIntentTimerRef.current = null;
          setActiveSubmenu(target);
        }, hoverMs());
      }
    },
    [navItems, activeSubmenu, clearSwitchIntentTimer],
  );

  const handleNavItemLeave = useCallback(() => {
    clearSwitchIntentTimer();
  }, [clearSwitchIntentTimer]);

  const handleNavLeave = useCallback(() => {
    clearSwitchIntentTimer();
    closeTimeoutRef.current = setTimeout(() => {
      setActiveSubmenu(null);
    }, hoverMs());
  }, [clearSwitchIntentTimer]);

  const handleExpanderEnter = useCallback(() => {
    clearSwitchIntentTimer();
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, [clearSwitchIntentTimer]);

  const handleExpanderLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setActiveSubmenu(null);
    }, hoverMs());
  }, []);

  /* ─── Close any open menu when the app navigates ───
     Sibling routes that share one route element (e.g. /shop/:menuSlug) re-render
     this Header instead of remounting it, so the drawer / mega-menu would stay
     open after a menu click. Brand headers pass the router's location key as
     `routeKey`; a change in it means the customer navigated. */
  const routeKeyRef = useRef(routeKey);
  useEffect(() => {
    if (routeKeyRef.current === routeKey) return;
    routeKeyRef.current = routeKey;
    clearSwitchIntentTimer();
    setActiveSubmenu(null);
    if (isMobileMenuOpen) closeMobileMenu();
    setIsSideMenuOpen(false);
  }, [routeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const rootClasses = [
    'ds-header',
    `ds-header--${layoutPreset.toLowerCase()}`,
    `ds-header--${mode}`,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  /* A brand's logo size is data (BRAND_CONTENT.logoWidth): handed to the layout as
     custom properties, the preset's own sizes stay the fallback (Header.css) */
  const logoWidthStyle = content.logoWidth
    ? {
      '--header-logo-width-mobile': content.logoWidth.mobile,
      '--header-logo-width-checkout-mobile': content.logoWidth.checkoutMobile,
      '--header-logo-width-desktop': content.logoWidth.desktop,
    }
    : undefined;

  const headerHidden = useHideOnScroll(hideOnScroll);

  /* Publish the header's height as --header-height on <html>, so pages that
     stack under a sticky header (a hero's viewport fit, a sticky summary)
     read the real value instead of copying 48 / 60px */
  const headerRef = useRef(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const root = document.documentElement;
    const publish = () => root.style.setProperty('--header-height', `${el.offsetHeight}px`);
    publish();
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(publish) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      root.style.removeProperty('--header-height');
    };
  }, []);

  const renderLogo = () => {
    const logoImage = <img src={logoSrc} alt="" aria-hidden="true" className="ds-header__logo-image" />;
    if (logoHref || onLogoClick) {
      return (
        <a
          href={logoHref || '#'}
          onClick={onLogoClick}
          className="ds-header__logo"
          aria-label={labels.logo}
        >
          {logoImage}
        </a>
      );
    }
    return (
      <div className="ds-header__logo" aria-label={labels.logo}>
        {logoImage}
      </div>
    );
  };

  const handleCartClick = useCallback(() => {
    if (cartItems.length === 0) {
      if (onCartCtaClick) onCartCtaClick();
      return;
    }
    setIsCartOpen((prev) => !prev);
  }, [cartItems.length, onCartCtaClick]);

  const renderCartButton = () => (
    <div className="ds-header__cart-wrap">
      <Button
        size="tertiary"
        variant="text"
        iconOnly
        iconLeft={<CartIcon />}
        className={`ds-header__icon-btn${isCartOpen ? ' ds-header__icon-btn--active' : ''}`}
        aria-label={labels.cart}
        onClick={handleCartClick}
      />
      <span className="ds-header__cart-badge typo-label">{cartCount}</span>

      {/* Cart Expander / Popup */}
      {isCartOpen && cartItems.length > 0 && (
        <div className="ds-header__cart-popup">
          {/* Header */}
          <div className="ds-header__cart-header">
            <span className="typo-h6">{cartCount} {cartItemsLabel || labels.items}</span>
            <Button
              size="tertiary"
              variant="white"
              iconOnly
              iconLeft={<X size={20} />}
              aria-label={labels.close}
              onClick={() => setIsCartOpen(false)}
            />
          </div>

          {/* Items */}
          <div className="ds-header__cart-items">
            {cartItems.map((item) => (
              <div key={item.id} className="ds-header__cart-item">
                <h4 className="ds-header__cart-item-name typo-h4">{item.name}</h4>
                <div className="ds-header__cart-item-row">
                  <div className="ds-header__cart-item-img-wrap">
                    {item.image && <img src={item.image} alt={item.name} className="ds-header__cart-item-img" />}
                  </div>
                  <div className="ds-header__cart-item-details">
                    {(item.details || []).map((line, i) => (
                      <p key={i} className="small typo-body-sm" style={{ marginTop: i === 0 ? 0 : '4px', marginBottom: 0 }}>{line}</p>
                    ))}
                  </div>
                  <div className="ds-header__cart-item-qty typo-body-sm">
                    {item.quantity}
                  </div>
                  <div className="ds-header__cart-item-price">
                    <Price price={item.price} type="small" />
                  </div>
                </div>
              </div>
            ))}

          </div>

          {/* Summary */}
          <div className="ds-header__cart-summary">
            <div className="ds-header__cart-summary-total">
              <span className="typo-h6">{cartTotalLabel}</span>
              <span className="typo-h6">{cartTotalProp || '0'}</span>
            </div>
          </div>

          {/* Footer CTA */}
          <div className="ds-header__cart-footer">
            <Button size="secondary" variant="main" iconRight={<ArrowRight size={20} />} onClick={() => { setIsCartOpen(false); if (onCartCtaClick) onCartCtaClick(); }}>
              {cartCtaLabel || labels.checkout}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  const renderWishlistButton = () => (
    <Button
      size="tertiary"
      variant="text"
      iconOnly
      iconLeft={<Heart size={20} />}
      className="ds-header__icon-btn ds-header__wishlist-btn"
      aria-label={wishlistLabel}
      onClick={onWishlistClick}
    />
  );

  const renderUserButton = () => (
    <Button
      size="tertiary"
      variant="text"
      iconOnly
      iconLeft={<UserRound size={20} />}
      className="ds-header__icon-btn ds-header__user-btn"
      aria-label={orderLabel}
      onClick={onUserClick}
    />
  );

  /* ─── Default mode ─── */
  const renderDefault = () => (
    <div className="ds-header__main-row">
      {/* Mobile: hamburger (hidden on desktop via CSS, except brands with a desktop drawer) */}
      <Button
        size="tertiary"
        variant={content.menuVariant || 'white'}
        iconOnly
        iconLeft={menuIcon}
        className={`ds-header__icon-btn ds-header__menu-btn${menuOnDesktop ? ' ds-header__menu-btn--desktop' : ''}`}
        aria-label={labels.menu}
        aria-haspopup={onMenuClick || sideMenu ? 'dialog' : undefined}
        aria-expanded={!onMenuClick && sideMenu ? isSideMenuOpen : undefined}
        onClick={onMenuClick || (sideMenu ? () => setIsSideMenuOpen(true) : openMobileMenu)}
      />

      {renderLogo()}

      {/* Desktop: nav links (hidden on mobile via CSS) */}
      <nav className="ds-header__nav" aria-label={labels.nav} onMouseLeave={handleNavLeave}>
        {navItems.map((item, index) => (
          <div
            key={item.label}
            className={`ds-header__nav-item${activeSubmenu === index ? ' ds-header__nav-item--active' : ''}`}
            onMouseEnter={() => handleNavEnter(index)}
            onMouseLeave={handleNavItemLeave}
          >
            <Hyperlink href={item.href || '#'} onClick={item.onClick} variant={content.navVariant} className="ds-header__nav-link">
              {item.label}
            </Hyperlink>
          </div>
        ))}
      </nav>

      {/* Desktop: school button + cart (hidden on mobile via CSS) */}
      <div className="ds-header__actions">
        {showSchoolButton && (
          <Button
            size="tertiary"
            variant="gray"
            className="ds-header__school-btn"
            onClick={onSchoolClick}
            title={schoolLabel || content.schoolLabel}
          >
            {/* Brand headers pass the selected school's name as schoolLabel —
                the span caps it at 150px with an ellipsis (see Header.css). */}
            <span className="ds-header__school-btn-label">{schoolLabel || content.schoolLabel}</span>
          </Button>
        )}
        {showWishlistButton && renderWishlistButton()}
        {showUserButton && renderUserButton()}
        {showCartButton && renderCartButton()}
      </div>

      {/* Mobile: cart only (hidden on desktop via CSS). The mobile logo is
          absolutely centred, so dropping the cart leaves the row balanced. */}
      {showCartButton && (
        <div className="ds-header__cart-mobile">
          {renderCartButton()}
        </div>
      )}
    </div>
  );

  /* ─── Checkout mode ─── */
  const renderCheckout = () => (
    <div className="ds-header__main-row">
      {/* Desktop: back button with label (hidden on mobile via CSS) */}
      <Button
        size="tertiary"
        variant="text"
        iconLeft={<ArrowLeft size={20} strokeWidth={1.8} />}
        className="ds-header__back-btn ds-header__back-desktop"
        onClick={onBackClick}
      >
        {backLabel}
      </Button>

      {/* Mobile: back icon only (hidden on desktop via CSS) */}
      <Button
        size="tertiary"
        variant="text"
        iconOnly
        iconLeft={<ArrowLeft size={20} strokeWidth={1.8} />}
        className="ds-header__icon-btn ds-header__back-mobile"
        aria-label={backLabel}
        onClick={onBackClick}
      />

      {renderLogo()}

      {/* Desktop: secure checkout label (hidden on mobile via CSS) */}
      <div className="ds-header__secure">
        <LockKeyhole size={24} strokeWidth={1.8} />
        <span className="typo-h6">{checkoutLabel}</span>
      </div>

      {/* Mobile: lock icon only (hidden on desktop via CSS) */}
      <Button
        size="tertiary"
        variant="text"
        iconOnly
        iconLeft={<LockKeyhole size={20} strokeWidth={1.8} />}
        className="ds-header__icon-btn ds-header__secure-mobile"
        aria-label={checkoutLabel}
      />
    </div>
  );

  /* ─── Expander (mega-menu) panel — Desktop only ─── */
  const renderExpander = () => {
    if (displayedSubmenu === null) return null;
    const submenuData = navItems[displayedSubmenu]?.submenu;
    if (!submenuData) return null;

    const expanderClass = [
      'ds-header__expander',
      isExpanderClosing && 'ds-header__expander--closing',
      isExpanderSwitching && 'ds-header__expander--switching',
    ].filter(Boolean).join(' ');

    return (
      <div
        className={expanderClass}
        onMouseEnter={handleExpanderEnter}
        onMouseLeave={handleExpanderLeave}
      >
        <div key={displayedSubmenu} className="ds-header__expander-inner">
          {submenuData.watermark && (
            <span className="ds-header__expander-watermark typo-h1-editorial">
              {submenuData.watermark}
            </span>
          )}
          <div className="ds-header__expander-columns">
            {submenuData.columns.map((col, i) => {
              const colStyle = { '--col-index': i };
              if (col.type === 'card') {
                return (
                  <div key={i} className="ds-header__expander-col" style={colStyle}>
                    <NavigationCard
                      image={col.image}
                      imageAlt={col.imageAlt || ''}
                      type={col.cardType || 'button'}
                      label={col.label}
                      href={col.href || '#'}
                      onClick={col.onClick}
                    />
                  </div>
                );
              }
              if (col.type === 'links') {
                return (
                  <div key={i} className="ds-header__expander-col ds-header__expander-links" style={colStyle}>
                    {col.items.map((link) => (
                      <Hyperlink
                        key={link.label}
                        href={link.href || '#'}
                        onClick={link.onClick}
                        variant="regular"
                        className="ds-header__expander-link"
                      >
                        {link.label}
                      </Hyperlink>
                    ))}
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      </div>
    );
  };

  /* ─── Mobile Drawer ─── */
  const renderMobileDrawer = () => {
    if (!isMobileMenuOpen) return null;

    const isSubmenu = mobileSubmenuIndex !== null;
    const submenuData = isSubmenu ? navItems[mobileSubmenuIndex]?.submenu : null;
    // Brands whose hamburger stays on desktop keep the drawer there too
    const desktopModifier = menuOnDesktop ? '--desktop' : null;

    return (
      <>
        {/* Overlay */}
        <div
          className={`ds-header__drawer-overlay${(drawerEntered || isSwiping) ? ' ds-header__drawer-overlay--entered' : ''}${isDrawerClosing ? ' ds-header__drawer-overlay--closing' : ''}${desktopModifier ? ` ds-header__drawer-overlay${desktopModifier}` : ''}`}
          onClick={closeMobileMenu}
          style={overlaySwipeStyle}
        />

        {/* Sidebar */}
        <aside
          className={`ds-header__drawer${(drawerEntered || isSwiping) ? ' ds-header__drawer--entered' : ''}${isDrawerClosing ? ' ds-header__drawer--closing' : ''}${desktopModifier ? ` ds-header__drawer${desktopModifier}` : ''}`}
          style={isDrawerClosing ? {} : drawerSwipeStyle}
          ref={drawerRef}
          aria-label={labels.mobileNav}
        >
          {/* Drawer Header */}
          <div className="ds-header__drawer-header">
            <div className="ds-header__drawer-header-title">
              {isSubmenu && (
                <Button
                  size="tertiary"
                  variant="white"
                  iconOnly
                  iconLeft={<ArrowLeft size={20} />}
                  className="ds-header__drawer-back"
                  aria-label={labels.back}
                  onClick={goBackMobileMenu}
                />
              )}
              <span className="ds-header__drawer-title typo-h3">
                {isSubmenu ? navItems[mobileSubmenuIndex]?.label : (mobileSettings?.mobileTitle || labels.menu)}
              </span>
            </div>
            <Button
              size="tertiary"
              variant="text"
              iconOnly
              iconLeft={<X size={20}  />}
              className="ds-header__icon-btn ds-header__drawer-close"
              aria-label={labels.closeMenu}
              onClick={closeMobileMenu}
            />
          </div>

          {/* Drawer Content */}
          <div
            key={mobileSubmenuIndex ?? 'root'}
            className={`ds-header__drawer-content${mobileMenuDirection === 'forward' ? ' ds-header__drawer-content--slide-forward' : ''}${mobileMenuDirection === 'back' ? ' ds-header__drawer-content--slide-back' : ''}`}
          >
            {!isSubmenu ? (
              /* ── Default view: promo + nav links ── */
              <>
                {/* Promo card — from mobileSettings.mobilePromo */}
                {mobileSettings?.mobilePromo && (
                  <div className="ds-header__drawer-promo">
                      <NavigationCard
                      image={mobileSettings.mobilePromo.image}
                      imageAlt={mobileSettings.mobilePromo.imageAlt || ''}
                      type={mobileSettings.mobilePromo.cardType || 'button'}
                      label={mobileSettings.mobilePromo.label}
                      href={mobileSettings.mobilePromo.href || '#'}
                      onClick={mobileSettings.mobilePromo.onClick}
                    />
                  </div>
                )}

                {/* Nav links list */}
                <ul className="ds-header__drawer-nav">
                  {navItems.map((item, index) => (
                    <li key={item.label} className="ds-header__drawer-nav-item">
                      {item.submenu ? (
                        <button
                          className="ds-header__drawer-nav-link"
                          onClick={() => openMobileSubmenu(index)}
                          type="button"
                        >
                          <span className="ds-header__drawer-link-text typo-link">
                            {item.label}
                          </span>
                          <ArrowRight size={20} strokeWidth={1.8} className="ds-header__drawer-arrow" />
                        </button>
                      ) : (
                        <a href={item.href || '#'} onClick={item.onClick} className="ds-header__drawer-nav-link">
                          <span className="ds-header__drawer-link-text typo-link">
                            {item.label}
                          </span>
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              /* ── Extended view: submenu content ── */
              <>
                {submenuData && (
                  <div className="ds-header__drawer-submenu-content">
                    {/* Submenu promo card (from submenu.mobilePromo) */}
                    {submenuData.mobilePromo && (
                      <NavigationCard
                        image={submenuData.mobilePromo.image}
                        imageAlt={submenuData.mobilePromo.imageAlt || ''}
                        type={submenuData.mobilePromo.cardType || 'button'}
                        label={submenuData.mobilePromo.label}
                        href={submenuData.mobilePromo.href || '#'}
                        onClick={submenuData.mobilePromo.onClick}
                        className="ds-header__drawer-card"
                      />
                    )}

                    {/* Button cards — only if no mobilePromo (avoid duplicates) */}
                    {!submenuData.mobilePromo && submenuData.columns
                      .filter((col) => col.type === 'card' && col.cardType !== 'link')
                      .map((col, i) => (
                          <NavigationCard
                          key={`btn-${i}`}
                          image={col.image}
                          imageAlt={col.imageAlt || ''}
                          type={col.cardType || 'button'}
                          label={col.label}
                          href={col.href || '#'}
                          onClick={col.onClick}
                          className="ds-header__drawer-card"
                        />
                      ))}

                    {/* Link cards + Text links — bordered list like Level 1 nav */}
                    {(() => {
                      const linkCards = submenuData.columns
                        .filter((col) => col.type === 'card' && col.cardType === 'link');
                      const textLinks = submenuData.columns
                        .filter((col) => col.type === 'links')
                        .flatMap((col) => col.items);
                      if (linkCards.length === 0 && textLinks.length === 0) return null;
                      return (
                        <ul className="ds-header__drawer-submenu-nav">
                          {linkCards.map((col, i) => (
                            <li key={`card-link-${i}`} className="ds-header__drawer-submenu-nav-item">
                              <Hyperlink
                                href={col.href || '#'}
                                onClick={col.onClick}
                                variant="regular"
                                showIconRight
                                className="ds-header__drawer-submenu-nav-link"
                              >
                                {col.label}
                              </Hyperlink>
                            </li>
                          ))}
                          {textLinks.map((link) => (
                            <li key={link.label} className="ds-header__drawer-submenu-nav-item">
                              <Hyperlink
                                href={link.href || '#'}
                                onClick={link.onClick}
                                variant="regular"
                                className="ds-header__drawer-submenu-nav-link"
                              >
                                {link.label}
                              </Hyperlink>
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Drawer Footer — school button (mirrors the desktop one: "choose
              school" or the selected school's name) + icon-only edit order */}
          {(showSchoolButton || showUserButton) && (
            <div className="ds-header__drawer-footer">
              {showSchoolButton && (
                <Button
                  size="secondary"
                  variant="gray"
                  iconLeft={<GraduationCap size={20} strokeWidth={1.8} />}
                  className="ds-header__drawer-school"
                  onClick={onSchoolClick}
                  title={schoolLabel || content.schoolLabel}
                >
                  <span className="ds-header__drawer-school-label">{schoolLabel || content.schoolLabel}</span>
                </Button>
              )}
              {showUserButton && (
                <Button
                  size="secondary"
                  variant="gray"
                  iconOnly
                  iconLeft={<UserRound size={20} strokeWidth={1.8} />}
                  className="ds-header__drawer-order"
                  aria-label={orderLabel}
                  title={orderLabel}
                  onClick={onUserClick}
                />
              )}
            </div>
          )}
        </aside>
      </>
    );
  };

  return (
    <>
      {showTopBar && <TopBar brand={brand} notice={topBarNotice} links={topBarLinks} />}
      <header ref={headerRef} style={logoWidthStyle} className={`${rootClasses} ds-header-sticky${headerHidden ? ' ds-header-sticky--hidden' : ''}`}>
        <div className="ds-header__main">
          {isCheckout ? renderCheckout() : renderDefault()}
        </div>
        {!isCheckout && renderExpander()}
      </header>
      {!isCheckout && renderMobileDrawer()}
      {/* Hamburger → DS SideMenu when its content is passed (Overall); the
          built-in drawer stays for brands without it */}
      {!isCheckout && sideMenu && !onMenuClick && (
        <SideMenu
          {...sideMenu}
          title={sideMenu.title ?? labels.menu}
          closeLabel={sideMenu.closeLabel ?? labels.closeMenu}
          isOpen={isSideMenuOpen}
          onClose={() => setIsSideMenuOpen(false)}
        />
      )}
    </>
  );
};

Header.propTypes = {
  brand: PropTypes.oneOf(['CLS', 'ABC', 'Overall', 'Crownstudent']),
  subBrand: PropTypes.oneOf(['main', 'clothing', 'default', 'Main', 'Clothing', 'Default']),
  mode: PropTypes.oneOf(['default', 'checkout']),
  /** Whether to show the TopBar string. If true, TopBar renders above header */
  showTopBar: PropTypes.bool,
  topBarNotice: PropTypes.string,
  topBarLinks: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, href: PropTypes.string })),
  /** Whether the header should auto-hide on scroll down */
  hideOnScroll: PropTypes.bool,
  cartCount: PropTypes.number,
  cartItems: PropTypes.array,
  cartTotal: PropTypes.string,
  cartItemsLabel: PropTypes.string,
  cartTotalLabel: PropTypes.string,
  cartCtaLabel: PropTypes.string,
  onCartCtaClick: PropTypes.func,
  /** Hamburger handler — when set, it replaces the built-in drawer AND `sideMenu` (the host opens its own menu) */
  onMenuClick: PropTypes.func,
  /** DS SideMenu content — when set, the hamburger opens the SideMenu instead of the built-in drawer
      (Overall). The Header owns its open state and closes it on `routeKey` changes; `title` /
      `closeLabel` default to `labels.menu` / `labels.closeMenu`. Same shape as the SideMenu props. */
  sideMenu: PropTypes.shape({
    title: PropTypes.node,
    closeLabel: PropTypes.string,
    featured: PropTypes.object,
    cards: PropTypes.arrayOf(PropTypes.object),
    links: PropTypes.arrayOf(PropTypes.object),
    actions: PropTypes.arrayOf(PropTypes.object),
    actionsOnDesktop: PropTypes.bool,
    closeOnNavigate: PropTypes.bool,
    className: PropTypes.string,
  }),
  /** Hamburger icon (default: the brand's — lucide Menu, Overall's custom bars) */
  menuIcon: PropTypes.node,
  /** Keep the hamburger (and the built-in drawer) on desktop too (default: brand — Overall on, others off) */
  menuOnDesktop: PropTypes.bool,
  /** Logo image URL (default: the brand's logotype from assets/logos) */
  logoSrc: PropTypes.string,
  /** Logo link target */
  logoHref: PropTypes.string,
  /** Logo click handler */
  onLogoClick: PropTypes.func,
  /** The header's own strings — aria-labels and minicart fallbacks: { cart, items, close, checkout, menu, back, closeMenu, nav, mobileNav, logo }. English defaults, the brand's language from BRAND_CONTENT, this prop on top */
  labels: PropTypes.shape({
    cart: PropTypes.string,
    items: PropTypes.string,
    close: PropTypes.string,
    checkout: PropTypes.string,
    menu: PropTypes.string,
    back: PropTypes.string,
    closeMenu: PropTypes.string,
    nav: PropTypes.string,
    mobileNav: PropTypes.string,
    logo: PropTypes.string,
  }),
  /** Callback for checkout back button click */
  onBackClick: PropTypes.func,
  /** Right-side label in checkout mode (e.g. localized "Secure Checkout") */
  checkoutLabel: PropTypes.string,
  /** Back button label in checkout mode (e.g. localized "Back") */
  backLabel: PropTypes.string,
  /** Nav items with optional submenu config. Falls back to BRAND_CONTENT strings. */
  navItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string,
      submenu: PropTypes.shape({
        watermark: PropTypes.string,
        mobilePromo: PropTypes.shape({
          cardType: PropTypes.oneOf(['button', 'link']),
          image: PropTypes.string,
          imageAlt: PropTypes.string,
          label: PropTypes.string,
          href: PropTypes.string,
        }),
        columns: PropTypes.arrayOf(
          PropTypes.shape({
            type: PropTypes.oneOf(['card', 'links']).isRequired,
            /* card props */
            cardType: PropTypes.oneOf(['button', 'link']),
            image: PropTypes.string,
            imageAlt: PropTypes.string,
            label: PropTypes.string,
            href: PropTypes.string,
            /* links props */
            items: PropTypes.arrayOf(
              PropTypes.shape({
                label: PropTypes.string.isRequired,
                href: PropTypes.string,
                onClick: PropTypes.func,
              }),
            ),
          }),
        ),
      }),
    }),
  ),
  /** Mobile drawer settings: promo card + title */
  mobileSettings: PropTypes.shape({
    mobileTitle: PropTypes.string,
    mobilePromo: PropTypes.shape({
      cardType: PropTypes.oneOf(['button', 'link']),
      image: PropTypes.string,
      imageAlt: PropTypes.string,
      label: PropTypes.string,
      href: PropTypes.string,
    }),
  }),
  /** Router location key — a change closes any open menu (see the effect) */
  routeKey: PropTypes.string,
  /** School button label (desktop header + mobile drawer footer) — brand headers pass the selected school's name; falls back to the brand's "choose school" copy */
  schoolLabel: PropTypes.string,
  /** Callback when school selector is clicked in mobile drawer */
  onSchoolClick: PropTypes.func,
  /** Whether to render the school selector button (desktop + drawer footer). Default: brand (Overall off, others on) */
  showSchoolButton: PropTypes.bool,
  /** Whether to render the wishlist (heart) button on desktop. Default: brand (Overall on, others off) */
  showWishlistButton: PropTypes.bool,
  /** Callback when the wishlist button is clicked */
  onWishlistClick: PropTypes.func,
  /** Accessible label for the wishlist button */
  wishlistLabel: PropTypes.string,
  /** Whether to render the user / edit-order button (desktop + drawer footer). Default: brand (Overall on, others off) */
  showUserButton: PropTypes.bool,
  /** Whether to render the cart button + minicart (desktop + mobile). Default true;
   *  false for sites without a shop (e.g. the ABC DK editorial site). */
  showCartButton: PropTypes.bool,
  /** Callback when the user / edit-order button is clicked */
  onUserClick: PropTypes.func,
  /** Accessible label (aria-label + title) of the icon-only user / edit-order button */
  orderLabel: PropTypes.string,
  className: PropTypes.string,
};

export default Header;